import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { Session, getSessionSubject } from '../Api/useSession';
import { getConfigurationApi } from '../Api/Configuration/configurationApi';
import { addError } from './Error';

export type Configuration = {
  REACT_APP_GOOGLE_MAPS_API_KEY: string;
};

export interface UseSharedConfigurationReturn {
  fetchConfiguration: () => Promise<void>;
}
export const defaultConfigurationData: Configuration = {
  REACT_APP_GOOGLE_MAPS_API_KEY: ''
};

const configurationSubject = new BehaviorSubject<Configuration>(defaultConfigurationData);
const configurationLoadingSubject = new BehaviorSubject<boolean>(false);
const emitErrorState = (message?: string): void => addError('configuration', message);

export const getConfiguration = (): Configuration => configurationSubject.value;

export const getConfigurationSubject = (): BehaviorSubject<Configuration> => configurationSubject;

export const useConfiguration = (): Configuration => {
  const [configuration, setConfiguration] = useState<Configuration>(getConfiguration());

  useEffect(() => {
    const sub = configurationSubject.subscribe(setConfiguration);

    return () => {
      if (sub) sub.unsubscribe();
    };
  }, []);

  return configuration;
};

export const useSharedConfiguration = (): UseSharedConfigurationReturn => {
  const [session, setSession] = useState<Session | undefined>(undefined);

  useEffect(() => {
    const sessionSub = getSessionSubject().subscribe((session) => setSession(session));

    return () => {
      if (sessionSub) sessionSub.unsubscribe();
    };
  }, []);

  const fetchConfiguration = async () => {
    configurationLoadingSubject.next(true);
    const { data, error } = await getConfigurationApi();
    if (data) {
      configurationSubject.next({ ...data });
    }

    if (error) {
      emitErrorState(error?.message);
    }

    configurationLoadingSubject.next(false);
  };

  useEffect(() => {
    if (session?.authenticated) {
      fetchConfiguration();
    }
  }, [session?.authenticated]);

  return { fetchConfiguration };
};
